import { memo, useCallback, useMemo } from "react";
import { useTheme } from "../../hooks/useTheme";
import { GetDateWithTime } from "@markit/common.utils";
import { CustomTicketV2, TicketReceipt, TicketV2 } from "@markit/common.types";
import TextHoverButton from "../Buttons/TextHoverButton";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";

// Combine ticketReceipts and mappedTickets into a single list sorted by createdAt
// Had to create a new type for this case because of how we want to display receipts for paid tickets and no receipts for free/request
// Potentially refactor and be able to utilize cleaner typing for these in the future.
type ReceiptItem =
  | { type: "receipt"; receipt: TicketReceipt }
  | { type: "mapped"; tickets: TicketV2[] };

type FullEventConfirmationReceiptsProps = {
  ticketReceipts: TicketReceipt[];
  mappedTickets: Map<string, TicketV2[]>;
  customTickets: CustomTicketV2[];
};

const FullEventConfirmationReceipts = memo(
  function FullEventConfirmationReceiptsFn(
    props: FullEventConfirmationReceiptsProps
  ) {
    const { ticketReceipts, mappedTickets, customTickets } = props;
    const { theme } = useTheme();

    const allItems: ReceiptItem[] = useMemo(
      () =>
        [
          ...ticketReceipts.map((receipt) => ({
            type: "receipt" as const,
            receipt,
            createdAt: receipt.createdAt,
          })),
          ...Array.from(mappedTickets.entries()).map(
            ([createdAt, tickets]) => ({
              type: "mapped" as const,
              tickets,
              createdAt,
            })
          ),
        ].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
      [mappedTickets, ticketReceipts]
    );

    // Lists out the custom ticket labels in the format "VIP (2), GA"
    const getCustomTicketLabelsFromTickets = useCallback(
      (tickets: TicketV2[]): string => {
        const countsByCustomTicketId = tickets.reduce<
          Record<string, number>
        >((acc, ticket) => {
          acc[ticket.customTicketId] = (acc[ticket.customTicketId] ?? 0) + 1;
          return acc;
        }, {});
        return Object.entries(countsByCustomTicketId)
          .map(([customTicketId, count]) => {
            const customTicket = customTickets.find(
              (tix) => tix.id === customTicketId
            );
            return customTicket
              ? count > 1
                ? `${customTicket.label} (${count})`
                : customTicket.label
              : null;
          })
          .filter((s): s is string => s !== null)
          .join(", ");
      },
      [customTickets]
    );

    const viewReceipt = useCallback((receipt: TicketReceipt) => {
      window.open(receipt.receiptUrl, "_blank", "noreferrer");
    }, []);

    const renderTicketItem = useCallback(
      (item: ReceiptItem) => {
        if (item.type === "receipt") {
          const customTicketLabels = getCustomTicketLabelsFromTickets(
            item.receipt.tickets
          );
          return (
            <div
              className="AlignedRowSpaced"
              style={{ gap: 14 }}
              key={item.receipt.receiptUrl}
            >
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="bodyMedium" style={theme.PrimaryText}>
                  {customTicketLabels}
                </span>
                <span className="bodySubtext">
                  {GetDateWithTime(item.receipt.createdAt, true)}
                </span>
              </div>
              <TextHoverButton
                text="Receipt"
                onPress={() => viewReceipt(item.receipt)}
                textStyles={{ fontSize: 14 }}
                iconPosition="right"
                icon={
                  <Icon
                    icon="ion:arrow-forward"
                    height={16}
                    style={{
                      transform: "rotateY(0deg) rotate(-45deg)",
                      color: Colors.GRAY1,
                      marginBottom: -2,
                    }}
                  />
                }
              />
            </div>
          );
        }

        const customTicketLabels = getCustomTicketLabelsFromTickets(
          item.tickets
        );
        if (!customTicketLabels) {
          return null;
        }
        return (
          <div
            className="ColumnNormal"
            style={{ gap: 4 }}
            key={`mapped-${item.tickets[0].createdAt}`}
          >
            <span className="bodyMedium" style={theme.PrimaryText}>
              {customTicketLabels}
            </span>
            <span className="bodySubtext">
              {GetDateWithTime(item.tickets[0].createdAt, true)}
            </span>
          </div>
        );
      },
      [getCustomTicketLabelsFromTickets, theme.PrimaryText, viewReceipt]
    );

    return (
      <div className="ColumnNormal" style={{ gap: 14, flex: 1 }}>
        {allItems.length > 0
          ? allItems.map((item) => renderTicketItem(item))
          : null}
      </div>
    );
  }
);

export default FullEventConfirmationReceipts;
